import { parseToDate } from "../../utils/dateUtils";

/** School tasks are dated in the school's time zone, whatever the time zone of the browser. */
export const SCHOOL_TIME_ZONE = "Europe/Amsterdam";

const wallClock = (date: Date) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: SCHOOL_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? "";
  return { date: `${part("year")}-${part("month")}-${part("day")}`, time: `${part("hour")}:${part("minute")}:${part("second")}` };
};

/** A task with only a date is stored as midnight in Amsterdam; such a start has no time of its own. */
export const isDateOnlyStart = (value: unknown): boolean => {
  const date = parseToDate(value);
  return Boolean(date) && wallClock(date as Date).time === "00:00:00";
};

/** The calendar day of a start in Amsterdam, as "YYYY-MM-DD". */
export const startDateInSchoolZone = (value: unknown): string | null => {
  const date = parseToDate(value);
  return date ? wallClock(date).date : null;
};

/**
 * Formats the start of a task in Amsterdam: the date with the given options, followed by the time,
 * except for a task with only a date (exactly 00:00 in Amsterdam), which shows no time.
 */
export const formatTaskStart = (
  value: unknown,
  locale: string,
  dateOptions: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" },
): string => {
  const date = parseToDate(value);
  if (!date) return "";
  const time: Intl.DateTimeFormatOptions = isDateOnlyStart(date)
    ? {}
    : dateOptions.dateStyle ? { timeStyle: "short" } : { hour: "2-digit", minute: "2-digit" };
  return new Intl.DateTimeFormat(locale, { ...dateOptions, ...time, timeZone: SCHOOL_TIME_ZONE }).format(date);
};
